import { InfoIcon } from '@/assets/icons/info-icons/info';
import { Banner } from '@/components/basicComponents/banner';
import Label from '@/components/basicComponents/label';
import RadioButton from '@/components/basicComponents/radioButton';
import Textarea from '@/components/basicComponents/textarea';
import ShowText from '@/components/dueDiligence-page/dueDiligenceRecord/showText';
import { themeVar } from '@/styles/theme';
import { Control, Controller } from 'react-hook-form';
import styled from 'styled-components';
import { IFieldPromptEditorForm } from '.';

interface FieldPromptEditorTemplateProps {
  control: Control<IFieldPromptEditorForm, any, IFieldPromptEditorForm>;
  radioButtonName: 'useCustomContextPrompt' | 'useCustomSummaryPrompt' | 'useStructuredOutputTemplate';
  textareaName: 'contextPrompt' | 'summaryPrompt' | 'outputStructure';
  title: string;
  textareaLabel: string;
  defaultAiPrompt?: string;
  defaultBannerDescription: string;
  customizeBannerDescription: string;
  selectedOption: string;
  radioButtonItems: string[];
}

export const FieldPromptEditorTemplate = ({
  control,
  radioButtonName,
  textareaName,
  title,
  textareaLabel,
  defaultAiPrompt,
  defaultBannerDescription,
  customizeBannerDescription,
  selectedOption,
  radioButtonItems
}: FieldPromptEditorTemplateProps) => {
  return (
    <div>
      <Controller
        name={radioButtonName}
        control={control}
        render={({ field: { value, onChange } }) => (
          <>
            <Label size="md">{title}</Label>
            <RadioButtonsWrap>
              {radioButtonItems.map((item) => (
                <RadioButton
                  key={item}
                  name={radioButtonName}
                  radioButtonText={item}
                  selectedOption={value ? radioButtonItems[1] : radioButtonItems[0]}
                  handleRadioChange={(e) => onChange(e.target.value === radioButtonItems[1])}
                />
              ))}
            </RadioButtonsWrap>
          </>
        )}
      />

      {selectedOption === radioButtonItems[0] && (
        <>
          <BannerWrapper>
            <Banner
              title=""
              description={defaultBannerDescription}
              icon={<InfoIcon width={26} height={26} fill={themeVar('header.base')} />}
              bgColor={themeVar('layer.base')}
              color={themeVar('font.base')}
              dividerColor={themeVar('border.base')}
            />
          </BannerWrapper>
          {defaultAiPrompt && (
            <DefaultAiPrompt>
              <ShowText>{defaultAiPrompt}</ShowText>
            </DefaultAiPrompt>
          )}
        </>
      )}

      {selectedOption === radioButtonItems[1] && (
        <>
          <BannerWrapper>
            <Banner
              title=""
              description={customizeBannerDescription}
              icon={<InfoIcon width={26} height={26} fill={themeVar('header.base')} />}
              bgColor={themeVar('layer.base')}
              color={themeVar('font.base')}
              dividerColor={themeVar('border.base')}
            />
          </BannerWrapper>
          <Controller
            name={textareaName}
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Please complete this field.'
              }
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <Textarea
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                size="md"
                label={textareaLabel}
                limitCharactersTo={1000}
                isRequiredField
                errorText={error?.message}
              />
            )}
          />
        </>
      )}
    </div>
  );
};

const RadioButtonsWrap = styled.label`
  display: flex;
  flex-direction: column;
`;

const BannerWrapper = styled.div`
  padding: 10px 0;
`;

const DefaultAiPrompt = styled.div`
  font-family: Blinker;
  font-size: 16px;
  line-height: 22.4px;
  padding: 10px 20px;
  color: ${({ theme }) => theme.font.base};
  background: ${({ theme }) => theme.layer.base};
`;
